import { Box, Button } from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import WorkspacePremiumOutlinedIcon from "@mui/icons-material/WorkspacePremiumOutlined";
import { useAuthGate } from "../../../hooks/useAuthGate";
import { joinAsDeveloper } from "../../PublicHeader";
import { usePanelTokens } from "../../../utils/panelTokens";
import { PUBLIC_BASE as LOG_PUBLIC_BASE } from "../logAnalyzer/paths";
import { ROUTES } from "../../../routes";
import { Link as RouterLink } from "react-router-dom";

// Course catalog. Static for now — there is no training backend yet.
const COURSES = [
  {
    track: "Foundations",
    title: "Onboard AI for Drones",
    level: "Beginner",
    length: "8 hours",
    desc: "How perception runs on a flying platform: sensors, timing, compute budgets, and why a model that works on your laptop stalls in the air.",
    outcomes: ["Sensor & compute pipeline", "Latency budgeting", "Dataset capture from flight"],
  },
  {
    track: "Modeling",
    title: "Detection & Tracking from the Air",
    level: "Intermediate",
    length: "12 hours",
    desc: "Train detection and tracking models on aerial imagery — annotation, augmentation for altitude and motion blur, and evaluation that reflects real missions.",
    outcomes: ["Aerial dataset curation", "Train & evaluate detectors", "Multi-object tracking"],
  },
  {
    track: "Optimization",
    title: "Model Compression for the Edge",
    level: "Intermediate",
    length: "10 hours",
    desc: "Quantization, pruning and distillation to fit a model inside the drone's thermal and power envelope without losing the accuracy that matters.",
    outcomes: ["INT8 quantization", "Pruning & distillation", "Accuracy/latency tradeoffs"],
  },
  {
    track: "Deployment",
    title: "Deploying Models on the Drone",
    level: "Advanced",
    length: "14 hours",
    desc: "Take a trained model to the companion computer: runtime packaging, autopilot integration, OTA rollout to a fleet, and rollback when a build misbehaves.",
    outcomes: ["Runtime packaging", "Autopilot integration", "Fleet OTA & rollback"],
  },
  {
    track: "Autonomy",
    title: "Closing the Loop: Vision to Flight",
    level: "Advanced",
    length: "12 hours",
    desc: "Wire model output into flight behaviour — follow, avoid, inspect — and validate the loop in simulation before it ever leaves the bench.",
    outcomes: ["Perception → control", "Simulation validation", "Failsafe design"],
  },
  {
    track: "Operations",
    title: "Flight Data & Model Debugging",
    level: "Intermediate",
    length: "6 hours",
    desc: "Use real flight logs to explain what the model did and why — regressions, drift, and the telemetry to record so the next failure is diagnosable.",
    outcomes: ["Log-driven debugging", "Drift detection", "Telemetry design"],
    href: LOG_PUBLIC_BASE,
  },
];

// Sequences through the catalog above — the NVIDIA "learning path" idea.
const PATHS = [
  {
    title: "Drone AI Engineer",
    desc: "Foundations → detection → compression → deployment. The full route from no onboard AI to a model flying on your own airframe.",
    courses: 4,
  },
  {
    title: "Autonomy Developer",
    desc: "For teams already shipping models. Closing the loop from perception to flight behaviour, validated in sim.",
    courses: 3,
  },
  {
    title: "Fleet Operator",
    desc: "Roll out, monitor and debug models across a fleet — OTA, telemetry, drift and rollback.",
    courses: 3,
  },
];

const BENEFITS = [
  ["Train on real flight data", "Every module uses actual logs and aerial datasets, not toy benchmarks."],
  ["Deploy on real hardware", "Labs run against Vecros companion computers — the same target as production."],
  ["Certificate on completion", "Pass the assessment and receive a verifiable Vecros certificate."],
  ["Hands-on labs", "Browser-based environments, no local GPU or CUDA setup required."],
  ["Instructor-led or self-paced", "Take it on your own schedule, or book a cohort for your team."],
  ["Built on the tools you use", "The same log analyzer, calculators and fleet tooling you already have."],
];

const STEPS = [
  ["Enroll", "Join as a developer and pick a course or a full learning path."],
  ["Build", "Work through hands-on labs against real flight data and hardware."],
  ["Deploy", "Ship your model to a drone and validate it in simulation, then in flight."],
  ["Get certified", "Pass the assessment to earn a verifiable Vecros certificate."],
];

/**
 * Public training page — the developer-education front door, linked from the
 * site header. Content is static: enrollment routes into the normal developer
 * sign-up, since there is no training backend yet.
 */
const Training = () => {
  const t = usePanelTokens();
  const { isAuthenticated } = useAuthGate();
  const accent = t.accent;

  const card = {
    p: 3,
    borderRadius: 2,
    background: t.surface,
    border: `1px solid ${t.border}`,
    transition: "border-color .15s, transform .15s",
  };
  const clickable = { ...card, cursor: "pointer", "&:hover": { borderColor: accent, transform: "translateY(-2px)" } };
  // Cards navigate, so they render as anchors — reset the anchor defaults.
  const linkReset = { textDecoration: "none", color: "inherit", display: "block", textAlign: "left" };
  const section = { px: { xs: 3, md: 7 }, py: { xs: 5, md: 6 }, maxWidth: 1200, mx: "auto" };
  const eyebrow = { fontSize: 11, letterSpacing: ".14em", color: t.textMuted, mb: 2.5 };

  const enroll = () => (isAuthenticated ? window.location.assign(ROUTES.developer) : joinAsDeveloper());

  return (
    <Box sx={{ background: t.ground, color: t.text }}>
      {/* Hero */}
      <Box
        sx={{
          px: { xs: 3, md: 7 },
          pt: { xs: 7, md: 10 },
          pb: { xs: 6, md: 8 },
          textAlign: "center",
          borderBottom: `1px solid ${t.border}`,
          background:
            t.ground === "#000"
              ? "radial-gradient(circle at 50% 20%, rgba(16,55,129,.30) 0%, rgba(4,9,26,.95) 60%, #000 100%)"
              : "radial-gradient(circle at 50% 20%, rgba(58,142,255,.14) 0%, rgba(248,250,252,.85) 55%, #fff 85%)",
        }}
      >
        <Box sx={{ fontSize: 11, letterSpacing: ".14em", color: accent, fontWeight: 700, mb: 2 }}>VECROS TRAINING</Box>
        <Box component="h1" sx={{ m: 0, fontSize: { xs: 34, md: 52 }, fontWeight: 700, lineHeight: 1.12, color: t.textStrong }}>
          Learn to put AI{" "}
          <Box component="span" sx={{ color: accent }}>
            on the drone.
          </Box>
        </Box>
        <Box component="p" sx={{ mt: 2.5, mb: 0, mx: "auto", maxWidth: 660, fontSize: { xs: 15, md: 18 }, color: t.textMuted, lineHeight: 1.6 }}>
          Hands-on courses that take you from training a model to running it onboard a flying aircraft — on real flight
          data, on real hardware. Finish the assessment and earn a Vecros certificate.
        </Box>
        <Box sx={{ mt: 4, display: "flex", justifyContent: "center", flexWrap: "wrap", gap: 1.5 }}>
          <Button
            onClick={enroll}
            variant="contained"
            sx={{ backgroundColor: accent, color: t.onAccent, textTransform: "none", fontWeight: 700, borderRadius: 2, px: 3.5, py: 1.2, "&:hover": { backgroundColor: accent, filter: "brightness(0.95)" } }}
          >
            {isAuthenticated ? "Access the app" : "Enroll as a developer"}
          </Button>
          <Button
            href="#courses"
            variant="outlined"
            sx={{ color: accent, borderColor: accent, textTransform: "none", fontWeight: 600, borderRadius: 2, px: 3, py: 1.2, "&:hover": { borderColor: accent, background: `${accent}14` } }}
          >
            Browse courses
          </Button>
        </Box>
        <Box sx={{ mt: 3, display: "flex", justifyContent: "center", flexWrap: "wrap", gap: 3, fontSize: 13, color: t.textMuted }}>
          <Box>✓ Real flight data</Box>
          <Box>✓ Deploy to real hardware</Box>
          <Box>✓ Certificate on completion</Box>
        </Box>
      </Box>

      {/* Learning paths */}
      <Box id="paths" sx={section}>
        <Box sx={eyebrow}>LEARNING PATHS</Box>
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 2.5 }}>
          {PATHS.map((p) => (
            <Box key={p.title} component="a" href="#courses" sx={{ ...clickable, display: "block", textDecoration: "none" }}>
              <Box sx={{ fontSize: 11, letterSpacing: ".12em", color: accent, fontWeight: 700, mb: 1 }}>
                {p.courses} COURSES
              </Box>
              <Box sx={{ fontSize: 17, fontWeight: 700, color: t.textStrong, mb: 1 }}>{p.title}</Box>
              <Box sx={{ fontSize: 13.5, color: t.textMuted, lineHeight: 1.6 }}>{p.desc}</Box>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Courses */}
      <Box id="courses" sx={{ ...section, borderTop: `1px solid ${t.border}` }}>
        <Box sx={eyebrow}>COURSE CATALOG</Box>
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 2.5 }}>
          {COURSES.map((c) => (
            <Box
              key={c.title}
              {...(c.href
                ? { component: RouterLink, to: c.href, sx: { ...clickable, ...linkReset } }
                : { component: "button", type: "button", onClick: enroll, sx: { ...clickable, ...linkReset, font: "inherit", width: "100%" } })}
            >
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mb: 1 }}>
                <Box sx={{ fontSize: 11, letterSpacing: ".12em", color: accent, fontWeight: 700 }}>
                  {c.track.toUpperCase()}
                </Box>
                <Box sx={{ fontSize: 11, color: t.chipText, background: t.chipBg, border: `1px solid ${t.chipBorder}`, borderRadius: 1, px: 0.8, py: 0.2 }}>
                  {c.level}
                </Box>
              </Box>
              <Box sx={{ fontSize: 17, fontWeight: 700, color: t.textStrong, mb: 1 }}>{c.title}</Box>
              <Box sx={{ fontSize: 13.5, color: t.textMuted, lineHeight: 1.6, mb: 2 }}>{c.desc}</Box>
              <Box sx={{ fontSize: 12.5, color: t.textMuted, lineHeight: 1.9, mb: 2 }}>
                {c.outcomes.map((o) => (
                  <Box key={o}>• {o}</Box>
                ))}
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: accent, fontSize: 13.5, fontWeight: 600 }}>
                  {c.href ? "Try the tool" : "Enroll"}
                  <ArrowForwardIcon sx={{ fontSize: 16 }} />
                </Box>
                <Box sx={{ fontSize: 12.5, color: t.textFaint }}>{c.length}</Box>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Certification */}
      <Box sx={{ borderTop: `1px solid ${t.border}` }}>
        <Box sx={section}>
          <Box sx={eyebrow}>CERTIFICATION</Box>
          <Box sx={{ ...card, display: "flex", flexWrap: "wrap", alignItems: "center", gap: 3, p: { xs: 3, md: 4 } }}>
            <WorkspacePremiumOutlinedIcon sx={{ fontSize: 56, color: accent }} />
            <Box sx={{ flex: "1 1 320px", minWidth: 0 }}>
              <Box sx={{ fontSize: 20, fontWeight: 700, color: t.textStrong, mb: 1 }}>
                Vecros Certified — Drone AI Deployment
              </Box>
              <Box sx={{ fontSize: 14, color: t.textMuted, lineHeight: 1.65 }}>
                Complete a learning path and pass the assessment — a build exercise that ends with your model running on a
                drone — to earn a verifiable certificate you can share with employers and clients. Certificates carry a
                unique ID and can be validated from your developer profile.
              </Box>
            </Box>
            <Button
              onClick={enroll}
              variant="contained"
              sx={{ backgroundColor: accent, color: t.onAccent, textTransform: "none", fontWeight: 700, borderRadius: 2, px: 3, py: 1.2, "&:hover": { backgroundColor: accent, filter: "brightness(0.95)" } }}
            >
              Start a path
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Why train with us */}
      <Box sx={{ borderTop: `1px solid ${t.border}` }}>
        <Box sx={section}>
          <Box sx={eyebrow}>WHY TRAIN WITH VECROS</Box>
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 1.5 }}>
            {BENEFITS.map(([title, desc]) => (
              <Box key={title} sx={{ ...card, p: 2 }}>
                <Box sx={{ fontSize: 14.5, fontWeight: 700, color: t.textStrong, mb: 0.5 }}>{title}</Box>
                <Box sx={{ fontSize: 12.5, color: t.textMuted, lineHeight: 1.5 }}>{desc}</Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* How it works + CTA */}
      <Box sx={{ borderTop: `1px solid ${t.border}` }}>
        <Box sx={section}>
          <Box sx={eyebrow}>HOW IT WORKS</Box>
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 2.5 }}>
            {STEPS.map(([title, desc], i) => (
              <Box key={title}>
                <Box sx={{ fontSize: 12, fontWeight: 700, color: accent, mb: 1 }}>STEP {i + 1}</Box>
                <Box sx={{ fontSize: 16, fontWeight: 700, color: t.textStrong, mb: 0.5 }}>{title}</Box>
                <Box sx={{ fontSize: 13.5, color: t.textMuted, lineHeight: 1.6 }}>{desc}</Box>
              </Box>
            ))}
          </Box>

          <Box sx={{ mt: 5, display: "flex", flexWrap: "wrap", alignItems: "center", gap: 2 }}>
            <Button
              onClick={enroll}
              variant="contained"
              sx={{ backgroundColor: accent, color: t.onAccent, textTransform: "none", fontWeight: 700, borderRadius: 2, px: 3.5, py: 1.2, "&:hover": { backgroundColor: accent, filter: "brightness(0.95)" } }}
            >
              {isAuthenticated ? "Access the app" : "Enroll as a developer"}
            </Button>
            <Box sx={{ fontSize: 13, color: t.textMuted }}>
              Training a team?{" "}
              <Box
                component="a"
                href="https://docs.vecros.com/"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ color: accent, textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
              >
                Read the docs
              </Box>{" "}
              while cohort scheduling opens up.
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Training;
